<template>
    <Head title="Reflections" />
    <BaseLayout class="bg-surface-sunken">
        <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-4">
                <h1 class="display-serif text-ink">Reflections</h1>
                <HorizontalDivider as="hr" spacing="none" />
            </div>

            <div v-if="loading" class="body-small text-ink-muted">Loading…</div>
            <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

            <div v-else-if="hub" class="flex flex-col gap-5 lg:flex-row lg:gap-10 lg:items-start">
                <aside class="shrink-0 flex flex-col gap-3 lg:gap-0">
                    <ReflectionsSideNav :active="activeTab" :show-admin="hub.is_assessment_admin" :tabs="hub.tabs" @select="onTabChange" />
                    <a v-if="hub.team_results_enabled" href="/reflections/team" class="base-btn-cream base-btn-sm w-full inline-flex items-center justify-center gap-2 lg:mt-4">
                        <i class="bi bi-people" aria-hidden="true"></i>
                        Team results
                    </a>
                </aside>

                <div class="view-viewport flex-1 min-w-0 overflow-hidden flex flex-col gap-5">
                    <p class="sr-only" role="status">{{ activeView.kind === "launching" ? "Launching your 360…" : "" }}</p>
                    <BaseNotification v-if="refreshFailed" theme="warning" hide-dismiss>
                        <template #content>
                            <div class="grow flex items-center justify-between gap-3 flex-wrap">
                                <span class="body-small">This list may be out of date — we couldn't refresh it.</span>
                                <BaseButton theme="neutral" size="sm" :disabled="refreshing" @click="refreshHub">
                                    {{ refreshing ? "Refreshing…" : "Refresh" }}
                                </BaseButton>
                            </div>
                        </template>
                    </BaseNotification>
                    <Transition :name="transitionName" mode="out-in">
                        <div :key="viewKey" class="flex flex-col gap-5">
                            <template v-if="activeView.kind === 'take-self'">
                                <SelfAssessTaker
                                    :cycle-id="activeView.cycleId"
                                    :title="activeView.title"
                                    :accent-color="activeView.accentColor"
                                    :accent-surface-color="activeView.accentSurfaceColor"
                                    :step="activeView.step"
                                    :closes-at="activeView.closesAt"
                                    :is-sixty="activeView.isSixty"
                                    @back="onExitInlineView"
                                    @submitted="onSelfSubmitted" />
                            </template>
                            <template v-else-if="activeView.kind === 'self-complete'">
                                <SelfAssessCompletePanel :cycle-id="activeView.cycleId" @back="onTabChange('upcoming')" @view="onTabChange('sixty')" />
                            </template>
                            <template v-else-if="activeView.kind === 'results'">
                                <ResultsPanel
                                    :cycle-id="activeView.cycleId"
                                    :show-suggested-goals="show_suggested_goals"
                                    :development-journeys-enabled="development_journeys_enabled"
                                    :assessment-goal-discussion-chat-start-url="assessment_goal_discussion_chat_start_url"
                                    :assessment-goal-adoption-url="assessment_goal_adoption_url"
                                    :assessment-goal-dismissal-url="assessment_goal_dismissal_url"
                                    @back="onExitInlineView"
                                    @open-self="onOpenSelfFrom360" />
                            </template>
                            <template v-else-if="activeView.kind === 'start-360'">
                                <Start360Panel :mode="activeView.mode" @back="onExitInlineView" @created="onCycleCreated" />
                            </template>
                            <template v-else-if="activeView.kind === 'nominate'">
                                <Nominate360Panel :cycle-id="activeView.cycleId" @back="onExitInlineView" @launched="onCycleLaunched" />
                            </template>
                            <template v-else-if="activeView.kind === 'launching'">
                                <Launch360Animation @done="onLaunchAnimationDone" />
                            </template>
                            <template v-else-if="activeView.kind === 'launched'">
                                <Launched360Panel
                                    :self-assess-enabled="activeView.selfAssessEnabled"
                                    :closes-at="activeView.closesAt"
                                    @start="onStartSelfAfterLaunch"
                                    @done="onExitInlineView" />
                            </template>
                            <template v-else-if="activeView.kind === 'new-campaign'">
                                <AdminNewCampaignPanel
                                    :templates="activeView.templates"
                                    @back="activeView.returnTo === 'drafts' ? onReturnToDrafts() : onExitInlineView()"
                                    @created="onCampaignCreated" />
                            </template>
                            <template v-else-if="activeView.kind === 'drafts'">
                                <AdminDraftsPanel @back="onExitInlineView" @new-campaign="onNewCampaignFromDrafts" @edit="onEditCampaign" />
                            </template>
                            <template v-else-if="activeView.kind === 'edit-campaign'">
                                <AdminNewCampaignPanel :templates="activeView.templates" :campaign="activeView.campaign" @back="onReturnToDrafts" @saved="onReturnToDrafts" />
                            </template>
                            <template v-else-if="activeView.kind === 'admin-campaign-results'">
                                <AdminCampaignResultsPanel :campaign-id="activeView.campaignId" @back="onExitInlineView" />
                            </template>
                            <template v-else-if="activeView.kind === 'admin-settings'">
                                <AdminSettingsPanel @back="onExitInlineView" />
                            </template>
                            <template v-else-if="activeView.kind === 'give-feedback'">
                                <TeamFeedbackPanel
                                    :invite-token="activeView.inviteToken"
                                    :subject-first-name="activeView.subjectFirstName"
                                    @back="onFeedbackFinished"
                                    @submitted="refreshHub" />
                            </template>
                            <UpcomingTab
                                v-else-if="activeTab === 'upcoming'"
                                :items="upcomingItems"
                                :tabs="hub.tabs"
                                :past-cycles="pastCycles"
                                :can-start-self="hub.can_start_self"
                                :can-start360="hub.can_start_360"
                                @start-self="onStartSelf"
                                @view-cycle="onViewCycle"
                                @nominate="onNominate"
                                @start-from-campaign="onStartFromCampaign"
                                @open-tab="onTabChange"
                                @give-feedback="onGiveFeedbackFromUpcoming"
                                @new-360="onStart360"
                                @start-blank="onStartSelfBlank" />
                            <SixtiesTab
                                v-else-if="activeTab === 'sixty'"
                                :active-cycles="hub.active_360s ?? []"
                                :past-cycles="hub.past_360s ?? []"
                                :pending-tasks="sixtyTasks"
                                :can-start="hub.can_start_360"
                                @remind="onRemind"
                                @wrap-up="onWrapUp"
                                @invite-more="onNominate"
                                @nominate="onNominate"
                                @start-from-campaign="onStartFromCampaign"
                                @view-cycle="onViewCycle"
                                @open-self="onOpenSelfFrom360"
                                @deleted="refreshHub"
                                @extended="onExtended"
                                @new-360="onStart360" />
                            <SelfReflectionTab
                                v-else-if="activeTab === 'self'"
                                :pending-tasks="selfTasks"
                                :past-reflections="hub.past_self_reflections ?? []"
                                :can-start="hub.can_start_self"
                                @start="onStartSelf"
                                @view-cycle="onViewCycle"
                                @start-from-campaign="onStartFromCampaign"
                                @deleted="refreshHub"
                                @start-blank="onStartSelfBlank" />
                            <TeamReflectionsTab v-else-if="activeTab === 'team'" :requests="hub.team_reflection_requests ?? []" @give-feedback="onGiveFeedback" />
                            <AdminTab
                                v-else-if="activeTab === 'admin' && hub.is_assessment_admin"
                                @new-campaign="onNewCampaign"
                                @view-campaign="onViewCampaign"
                                @settings="onAdminSettings"
                                @drafts="onDrafts" />
                        </div>
                    </Transition>
                </div>
            </div>
        </div>
    </BaseLayout>
    <AssessmentDemoTools v-if="hub" hub-fallback bootstrap-kind="self" :cycle-actions="hubDemoActions" @deleted="refreshHub" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleForSubject, HubPayload, TeamReflectionRequest } from "~vue/api/generated/reflections";
import type { AdminCampaignSummary, TemplateSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import AdminCampaignResultsPanel from "~vue/pages/reflections/AdminCampaignResultsPanel.vue";
import AdminDraftsPanel from "~vue/pages/reflections/AdminDraftsPanel.vue";
import AdminNewCampaignPanel from "~vue/pages/reflections/AdminNewCampaignPanel.vue";
import AdminSettingsPanel from "~vue/pages/reflections/AdminSettingsPanel.vue";
import AdminTab from "~vue/pages/reflections/AdminTab.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import Launch360Animation from "~vue/pages/reflections/Launch360Animation.vue";
import Launched360Panel from "~vue/pages/reflections/Launched360Panel.vue";
import Nominate360Panel from "~vue/pages/reflections/Nominate360Panel.vue";
import ReflectionsSideNav, { isTabAvailable, type ReflectionsTabKey } from "~vue/pages/reflections/ReflectionsSideNav.vue";
import ResultsPanel from "~vue/pages/reflections/ResultsPanel.vue";
import SelfAssessCompletePanel from "~vue/pages/reflections/SelfAssessCompletePanel.vue";
import SelfAssessTaker from "~vue/pages/reflections/SelfAssessTaker.vue";
import SelfReflectionTab from "~vue/pages/reflections/SelfReflectionTab.vue";
import SixtiesTab from "~vue/pages/reflections/SixtiesTab.vue";
import Start360Panel from "~vue/pages/reflections/Start360Panel.vue";
import TeamFeedbackPanel from "~vue/pages/reflections/TeamFeedbackPanel.vue";
import TeamReflectionsTab from "~vue/pages/reflections/TeamReflectionsTab.vue";
import type { UpcomingItem } from "~vue/pages/reflections/UpcomingTab.vue";
import UpcomingTab from "~vue/pages/reflections/UpcomingTab.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";

export interface Props {
    // Server-computed from either Journeys flag so we never mount
    // SuggestedGoalsSection for users who cannot use either experience.
    show_suggested_goals?: boolean;
    development_journeys_enabled?: boolean;
    assessment_goal_discussion_chat_start_url?: string | null;
    assessment_goal_adoption_url?: string | null;
    assessment_goal_dismissal_url?: string | null;
}

const {
    show_suggested_goals = false,
    development_journeys_enabled = false,
    assessment_goal_discussion_chat_start_url = null,
    assessment_goal_adoption_url = null,
    assessment_goal_dismissal_url = null,
} = defineProps<Props>();

// The inline view state — when the user clicks Start reflection / View, the
// content pane replaces the list with the appropriate embedded flow. The
// list state (`kind: 'list'`) is the default; the sidebar tab picks WHICH
// list is shown.
type ActiveView =
    | { kind: "list" }
    | {
          kind: "take-self";
          cycleId: string;
          title: string;
          closesAt: string | null;
          accentColor?: string;
          accentSurfaceColor?: string;
          step?: string;
          isSixty?: boolean;
      }
    | { kind: "self-complete"; cycleId: string }
    | { kind: "results"; cycleId: string }
    | { kind: "start-360"; mode: "self" | "360" }
    | { kind: "nominate"; cycleId: string }
    | { kind: "launching"; cycleId: string; title: string; selfAssessEnabled: boolean; closesAt: string | null }
    | { kind: "launched"; cycleId: string; title: string; selfAssessEnabled: boolean; closesAt: string | null }
    // `returnTo` sends the form back to the Drafts subpage it was opened from.
    | { kind: "new-campaign"; templates: TemplateSummary[]; returnTo?: "drafts" }
    | { kind: "drafts" }
    | { kind: "edit-campaign"; campaign: AdminCampaignSummary; templates: TemplateSummary[] }
    | { kind: "admin-campaign-results"; campaignId: string }
    | { kind: "admin-settings" }
    | { kind: "give-feedback"; inviteToken: string; subjectFirstName: string };

const SIXTY_ACCENT = {
    accentColor: "var(--color-chip-three-sixty-text)",
    accentSurfaceColor: "var(--color-chip-three-sixty-bg)",
    isSixty: true,
} as const;

const hub = ref<HubPayload | null>(null);
const pastCycles = computed(() => [...(hub.value?.past_self_reflections ?? []), ...(hub.value?.past_360s ?? [])]);
const hubDemoActions = computed(() => {
    const active = (hub.value?.active_360s ?? []).map((cycle) => ({
        id: cycle.id,
        label: cycle.template.name,
        canFill: cycle.state === "draft" || cycle.state === "collecting",
    }));
    const pending = (hub.value?.pending_personal_tasks ?? [])
        .filter((task) => task.kind === "self_reflection_cycle" || task.kind === "cycle_setup")
        .map((task) => ({ id: task.id, label: task.title, canFill: true }));
    const past = [...(hub.value?.past_360s ?? []), ...(hub.value?.past_self_reflections ?? [])].map((cycle) => ({
        id: cycle.id,
        label: cycle.template.name,
        canFill: false,
    }));
    return [...new Map([...active, ...pending, ...past].map((action) => [action.id, action])).values()];
});
const loading = ref(true);
const loadError = ref<string | null>(null);
const refreshing = ref(false);
const refreshFailed = ref(false);
// A refresh asked for while one is on the wire cannot just be dropped: the
// in-flight request was issued before whatever prompted the second ask, so its
// answer is already stale. Not a ref — nothing renders it, `refreshing` covers
// the spinner for the whole burst.
let refreshQueued = false;
const requestedTab = ref<ReflectionsTabKey>(initialTabFromLocation());
// A tab the org has turned off is unreachable even via ?tab=.
const activeTab = computed<ReflectionsTabKey>(() => (isTabAvailable(requestedTab.value, hub.value?.tabs) ? requestedTab.value : "upcoming"));
const activeView = ref<ActiveView>({ kind: "list" });
const transitionName = ref<"hub-forward" | "hub-back">("hub-forward");

// The hub is a single Inertia page that swaps ~15 in-page views, so a click
// into a reflection used to be invisible to the browser and Back left
// /reflections/ altogether. `activeView` lives in history.state now.
//
// Inertia keeps its own `page` payload in history.state and, on popstate,
// remounts the page component from it, so every write below spreads the
// existing state and namespaces ours under one key. The fresh mount then
// reads our entry back in onMounted; there is no popstate handler of our own.
const HUB_STATE_KEY = "reflectionsHub";
const HUB_STATE_VERSION = 1;

interface HubHistoryState {
    v: number;
    tab: ReflectionsTabKey;
    view: ActiveView;
    // How many entries this shell has pushed, so the in-page Back button can
    // tell "pop one of ours" from "this would exit the site".
    depth: number;
}

// Fields each view needs to rebuild itself. An entry from an earlier deploy,
// or one whose data never existed in the hub payload, falls back to the list
// rather than mounting a panel with missing props.
const REQUIRED_VIEW_FIELDS: Record<ActiveView["kind"], readonly string[]> = {
    list: [],
    "take-self": ["cycleId", "title"],
    "self-complete": ["cycleId"],
    results: ["cycleId"],
    "start-360": ["mode"],
    nominate: ["cycleId"],
    launching: ["cycleId", "title"],
    launched: ["cycleId", "title"],
    "new-campaign": ["templates"],
    drafts: [],
    "edit-campaign": ["campaign", "templates"],
    "admin-campaign-results": ["campaignId"],
    "admin-settings": [],
    "give-feedback": ["inviteToken", "subjectFirstName"],
};

// Views that only an assessment admin may see; a restored one must not
// outlive the admin access it was opened with.
const ADMIN_VIEW_KINDS: ReadonlySet<ActiveView["kind"]> = new Set(["new-campaign", "drafts", "edit-campaign", "admin-campaign-results", "admin-settings"]);

function isTabKey(value: unknown): value is ReflectionsTabKey {
    return value === "upcoming" || value === "sixty" || value === "self" || value === "team" || value === "admin";
}

function parseView(raw: unknown): ActiveView | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const candidate = raw as Record<string, unknown>;
    const kind = candidate.kind;
    if (typeof kind !== "string" || !(kind in REQUIRED_VIEW_FIELDS)) {
        return null;
    }
    if (REQUIRED_VIEW_FIELDS[kind as ActiveView["kind"]].some((field) => candidate[field] === undefined || candidate[field] === null)) {
        return null;
    }
    if ((kind === "new-campaign" || kind === "edit-campaign") && !Array.isArray(candidate.templates)) {
        return null;
    }
    if (kind === "edit-campaign" && typeof candidate.campaign !== "object") {
        return null;
    }
    // Earned by the checks above: the kind is one of ours and carries the
    // fields its panel reads.
    return candidate as unknown as ActiveView;
}

function parseHubState(raw: unknown): HubHistoryState | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const entry = (raw as Record<string, unknown>)[HUB_STATE_KEY];
    if (!entry || typeof entry !== "object") {
        return null;
    }
    const { v, tab, view, depth } = entry as Record<string, unknown>;
    if (v !== HUB_STATE_VERSION || !isTabKey(tab)) {
        return null;
    }
    const parsedView = parseView(view);
    if (parsedView === null) {
        return null;
    }
    return { v: HUB_STATE_VERSION, tab, view: parsedView, depth: typeof depth === "number" ? depth : 0 };
}

function hubDepth(): number {
    return parseHubState(globalThis.history.state)?.depth ?? 0;
}

function urlForTab(tab: ReflectionsTabKey): string {
    const url = new URL(globalThis.location.href);
    if (tab === "upcoming") {
        url.searchParams.delete("tab");
    } else {
        url.searchParams.set("tab", tab);
    }
    // The view stays out of the URL on purpose: every in-hub view already has
    // a canonical standalone route (/reflections/360/<id>/results and
    // friends), so a second URL for the same content would only add
    // stale-link cases for views whose data isn't rebuildable from an id.
    return url.toString();
}

// Spreading the existing state keeps Inertia's own `page` entry alongside ours.
// That matters beyond politeness: on popstate Inertia v2 treats an entry that
// carries `page` as one of its own and remounts this component with
// preserveState=false, which is why onMounted's parseHubState is the restore
// path that actually runs in a browser. Dropping `page` would leave Inertia
// with nothing to remount from. Pending a manual browser check.
function writeHistory(view: ActiveView, tab: ReflectionsTabKey, replace: boolean) {
    // Views carry objects lifted straight out of reactive state (templates, a
    // campaign), and history.state can't structured-clone a Vue proxy. A JSON
    // round trip strips the proxies; every view field is plain data.
    const plainView = JSON.parse(JSON.stringify(view)) as ActiveView;
    const entry: HubHistoryState = { v: HUB_STATE_VERSION, tab, view: plainView, depth: replace ? hubDepth() : hubDepth() + 1 };
    const state = { ...(globalThis.history.state ?? {}), [HUB_STATE_KEY]: entry };
    if (replace) {
        globalThis.history.replaceState(state, "", urlForTab(tab));
    } else {
        globalThis.history.pushState(state, "", urlForTab(tab));
    }
}

/**
 * The one place a view change is written. Direction follows the target: any
 * non-list view is a step forward, returning to a list is a step back.
 */
function navigateTo(view: ActiveView, options: { replace?: boolean; tab?: ReflectionsTabKey; back?: boolean } = {}) {
    transitionName.value = view.kind === "list" || options.back ? "hub-back" : "hub-forward";
    activeView.value = view;
    if (options.tab !== undefined) {
        requestedTab.value = options.tab;
    }
    writeHistory(view, options.tab ?? requestedTab.value, options.replace ?? false);
}
// Key that changes whenever the visible view changes so Vue's <Transition
// mode="out-in"> knows to run the leave/enter cycle. Combines tab + kind + id.
const viewKey = computed(() => {
    const view = activeView.value;
    switch (view.kind) {
        case "list":
            return `list:${activeTab.value}`;
        case "start-360":
            return `start-360:${view.mode}`;
        case "new-campaign":
            return "new-campaign";
        case "drafts":
            return "drafts";
        case "edit-campaign":
            return `edit-campaign:${view.campaign.id}`;
        case "admin-campaign-results":
            return `admin-campaign-results:${view.campaignId}`;
        case "admin-settings":
            return "admin-settings";
        case "give-feedback":
            return `give-feedback:${view.inviteToken}`;
        default:
            return `${view.kind}:${view.cycleId}`;
    }
});

onMounted(async () => {
    // Re-entering the hub through forward/back lands on an entry we wrote
    // earlier, so adopt it instead of resetting to the list.
    const restored = parseHubState(globalThis.history.state);
    if (restored === null) {
        writeHistory({ kind: "list" }, requestedTab.value, true);
    } else {
        requestedTab.value = restored.tab;
        activeView.value = restored.view;
        // Inertia rewrites history.state after mount, which dropped our entry
        // and sent the next reload back to the list. Writing it again keeps
        // the restored view through further reloads.
        writeHistory(restored.view, restored.tab, true);
    }
    const { data, error: fetchError } = await useFetch("/v1/reflections/hub").json<HubPayload>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load reflections.";
        return;
    }
    hub.value = data.value;
    if (ADMIN_VIEW_KINDS.has(activeView.value.kind) && !hub.value?.is_assessment_admin) {
        // The entry came from history.state, which can't know access was
        // revoked since it was written; the fresh payload is authoritative.
        navigateTo({ kind: "list" }, { replace: true, tab: "upcoming" });
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_HUB_VIEWED, {
        feature_enabled: hub.value?.feature_enabled ?? false,
        in_progress_count: hub.value?.in_progress_cycles.length ?? 0,
    });
});

function initialTabFromLocation(): ReflectionsTabKey {
    if (typeof globalThis.location === "undefined") {
        return "upcoming";
    }
    const params = new URLSearchParams(globalThis.location.search);
    const tab = params.get("tab");
    if (tab === "sixty" || tab === "self" || tab === "team" || tab === "upcoming" || tab === "admin") {
        return tab;
    }
    return "upcoming";
}

function onTabChange(tab: ReflectionsTabKey) {
    if (tab === activeTab.value) {
        // Re-selecting the section you're already in is the way out of an
        // inline take/results panel — otherwise the nav item looks active but
        // does nothing while the user is mid-flow.
        if (activeView.value.kind !== "list") {
            onExitInlineView();
        }
        return;
    }
    // Switching tabs always exits an inline view, and pushes so Back steps
    // through prior sub-tabs instead of leaving the hub in one press.
    navigateTo({ kind: "list" }, { tab });
    // Only tab changes are an Amplitude event; view pushes are not.
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_HUB_TAB_CHANGED, { tab });
}

function onStartSelf(payload: { cycleId: string; title: string; closesAt: string | null }) {
    navigateTo({ kind: "take-self", cycleId: payload.cycleId, title: payload.title, closesAt: payload.closesAt });
}

// The subject completing their self-portion of a 360 gets the 360 pink accent
// so the cycle's visual identity carries through the flow — differentiates from
// self-only Self Reflection cycles, which keep the default warm accent.
function onOpenSelfFrom360(payload: { cycleId: string; title: string; closesAt: string | null }) {
    navigateTo({
        kind: "take-self",
        cycleId: payload.cycleId,
        title: payload.title,
        closesAt: payload.closesAt,
        ...SIXTY_ACCENT,
    });
}

function onViewCycle(cycleId: string) {
    navigateTo({ kind: "results", cycleId });
}

function onExitInlineView() {
    // The on-screen Back and the browser's Back have to mean the same thing,
    // so pop the entry we pushed rather than writing another one. Inertia
    // remounts the hub on popstate, and the fresh mount re-reads the payload.
    if (hubDepth() > 0) {
        globalThis.history.back();
        return;
    }
    // Nothing of ours to pop (the user landed straight in a view), so going
    // back would leave the site. Rewrite this entry as the list instead.
    navigateTo({ kind: "list" }, { replace: true });
    // Refresh so a state change made inside the inline view (report generated
    // on ResultsPanel, cycle launched from Nominate, campaign updated on
    // Admin) doesn't leave a stale row like a "Generating…" card.
    void refreshHub();
}

function onStart360() {
    navigateTo({ kind: "start-360", mode: "360" });
}

function onStartSelfBlank() {
    // Educational card on the Self tab: kick off the standalone
    // "Start a self assessment" flow inline.
    navigateTo({ kind: "start-360", mode: "self" });
}

function onNominate(cycleId: string) {
    navigateTo({ kind: "nominate", cycleId });
}

function onNewCampaign(templates: TemplateSummary[]) {
    navigateTo({ kind: "new-campaign", templates });
}

function onViewCampaign(campaignId: string) {
    navigateTo({ kind: "admin-campaign-results", campaignId });
}

function onDrafts() {
    navigateTo({ kind: "drafts" });
}

function onNewCampaignFromDrafts(templates: TemplateSummary[]) {
    navigateTo({ kind: "new-campaign", templates, returnTo: "drafts" });
}

function onEditCampaign(campaign: AdminCampaignSummary, templates: TemplateSummary[]) {
    navigateTo({ kind: "edit-campaign", campaign, templates });
}

function onReturnToDrafts() {
    // The form was pushed on top of the Drafts entry. Writing another Drafts
    // entry in its place would make the panel's Admin link land on Drafts
    // again, so overwrite the form entry (Forward can't reopen a saved form)
    // and pop back to the Drafts entry beneath it; the remount refetches.
    if (hubDepth() > 1) {
        writeHistory({ kind: "drafts" }, requestedTab.value, true);
        globalThis.history.back();
        return;
    }
    navigateTo({ kind: "drafts" }, { replace: true, back: true });
}

function onAdminSettings() {
    navigateTo({ kind: "admin-settings" });
}

function onGiveFeedback(request: TeamReflectionRequest) {
    navigateTo({ kind: "give-feedback", inviteToken: request.invite_token, subjectFirstName: request.subject_first_name });
}

// Same panel as the Team Reflections tab's Give feedback, but sourced from
// an Upcoming row's inviteToken + subjectFirstName rather than a full
// TeamReflectionRequest. Kept as its own handler so payload shapes stay
// clean at each call site.
function onGiveFeedbackFromUpcoming(payload: { inviteToken: string; subjectFirstName: string }) {
    navigateTo({ kind: "give-feedback", inviteToken: payload.inviteToken, subjectFirstName: payload.subjectFirstName });
}

// Submitting only refreshes the hub data — the panel keeps the inline view for
// its sending animation and "Feedback submitted" state until Done is clicked.
async function onFeedbackFinished() {
    // Replace, not push: the feedback is submitted, so Back must not reopen
    // the panel that sent it.
    navigateTo({ kind: "list" }, { replace: true, tab: "team" });
    void refreshHub();
}

function onCampaignCreated() {
    if (activeView.value.kind === "new-campaign" && activeView.value.returnTo === "drafts") {
        onReturnToDrafts();
        return;
    }
    // AdminTab re-fetches campaigns on mount, and switching viewKey back to
    // `list:admin` remounts it — so the new campaign will appear naturally.
    // Replace, so Back can't return to the create form for a campaign that
    // already exists.
    navigateTo({ kind: "list" }, { replace: true, tab: "admin" });
}

async function onCycleCreated(payload: { cycleId: string; isSelfAssessmentOnly: boolean; selfAssessEnabled: boolean; title: string; closesAt: string | null }) {
    // Self-only cycles are auto-launched by the API on create — go straight
    // to the take flow. 360 cycles land on the DRAFT view of Nominate so the
    // subject can invite raters + launch.
    // Replace: the cycle exists now, so Back into the create form would let
    // the user make a second one.
    if (payload.isSelfAssessmentOnly) {
        navigateTo({ kind: "take-self", cycleId: payload.cycleId, title: payload.title, closesAt: payload.closesAt }, { replace: true });
    } else {
        navigateTo({ kind: "nominate", cycleId: payload.cycleId }, { replace: true });
    }
    // Refresh hub payload in the background so the list catches the new cycle
    // when the user eventually exits back to it.
    void refreshHub();
}

async function onCycleLaunched(payload: { cycleId: string; selfAssessEnabled: boolean; title: string; closesAt: string | null }) {
    // The brand-mark interstitial plays first, then the confirmation screen
    // hands the subject over to their own self-assessment.
    // Replace: the cycle is launched, so that nominate screen is spent.
    navigateTo(
        {
            kind: "launching",
            cycleId: payload.cycleId,
            title: payload.title,
            selfAssessEnabled: payload.selfAssessEnabled,
            closesAt: payload.closesAt,
        },
        { replace: true },
    );
    void refreshHub();
}

function onLaunchAnimationDone() {
    if (activeView.value.kind !== "launching") {
        return;
    }
    const { cycleId, title, selfAssessEnabled, closesAt } = activeView.value;
    // Replace: a three-second interstitial must never be a history entry.
    navigateTo({ kind: "launched", cycleId, title, selfAssessEnabled, closesAt }, { replace: true });
}

function onStartSelfAfterLaunch() {
    if (activeView.value.kind !== "launched") {
        return;
    }
    const { cycleId, title, closesAt } = activeView.value;
    // Push: the confirmation screen is idempotent, so backing out of the
    // taker onto it is harmless.
    navigateTo({ kind: "take-self", cycleId, title, closesAt, step: "Step 3 of 3", ...SIXTY_ACCENT });
}

// Every caller fires this in the background, so a dropped refresh surfaces as
// "I submitted it and the list still says pending". Failures get a visible,
// retryable banner instead of leaving the list quietly stale.
async function refreshHub() {
    if (refreshing.value) {
        refreshQueued = true;
        return;
    }
    refreshing.value = true;
    try {
        // Loops rather than recurses so `refreshing` stays true across a burst
        // and the banner's button stays disabled throughout. Only a user action
        // sets the flag, and each pass is a round trip, so it cannot spin.
        do {
            refreshQueued = false;
            const { data, error: fetchError } = await useFetch("/v1/reflections/hub").json<HubPayload>();
            if (fetchError.value || !data.value) {
                refreshFailed.value = true;
            } else {
                hub.value = data.value;
                refreshFailed.value = false;
            }
        } while (refreshQueued);
    } finally {
        refreshing.value = false;
        refreshQueued = false;
    }
}

async function onSelfSubmitted() {
    // A self-only reflection unlocks on submit, so the user lands on the
    // results panel, which polls until the report is ready — a coherent
    // "processing → results" flow rather than a stale list with a "Generating…"
    // row. The self leg of a 360 has no results to show while raters are still
    // responding, so it gets the confirmation panel instead. Refresh the hub in
    // the background either way so the list is current when they navigate back.
    // Replace either way: the response is submitted, so Back must not reopen
    // the taker for it.
    const view = activeView.value;
    if (view.kind !== "take-self") {
        navigateTo({ kind: "list" }, { replace: true });
    } else {
        navigateTo({ kind: view.isSixty ? "self-complete" : "results", cycleId: view.cycleId }, { replace: true });
    }
    void refreshHub();
}

const upcomingItems = computed<UpcomingItem[]>(() => {
    if (!hub.value) {
        return [];
    }
    const items: UpcomingItem[] = [];
    for (const req of hub.value.team_reflection_requests ?? []) {
        items.push({
            id: `team-${req.invite_token}`,
            kind: "team_reflection",
            title: `Give 360 feedback to ${req.subject_first_name}`,
            subtitle: `Requested by ${req.subject_first_name} · Anonymous`,
            chipLabel: "Team Review",
            ctaLabel: "Give feedback",
            href: `/reflections/360/r/${req.invite_token}/`,
            closesAt: req.closes_at ?? null,
            inviteToken: req.invite_token,
            subjectFirstName: req.subject_first_name,
        });
    }
    for (const task of hub.value.pending_personal_tasks ?? []) {
        // `task.id` is either a cycle id or a campaign id depending on kind.
        // The row keeps both fields distinct so downstream handlers know
        // whether to route through /cycles/{id} or /cycles/from-campaign.
        const kind = mapTaskKind(task.kind, task.is_self_assessment_only ?? true);
        const isCampaign = kind === "self_reflection_campaign" || kind === "cycle_setup_campaign";
        // Matches SelfTaskRow: saved answers mean the user is picking up where
        // they left off, so the row says "Resume" rather than "Start".
        const selfVerb = (task.answered_count ?? 0) > 0 ? "Resume" : "Start";
        items.push({
            id: `task-${task.id}`,
            kind,
            title: task.is_self_assessment_only ? `${selfVerb} your ${task.title}` : `Finish setup for ${task.title}`,
            subtitle: task.subtitle ?? "",
            chipLabel: task.is_self_assessment_only ? "Self Reflection" : "360",
            ctaLabel: task.is_self_assessment_only ? `${selfVerb} reflection` : "Continue",
            href: hrefForTask({ kind: task.kind, id: task.id, is_self_assessment_only: task.is_self_assessment_only ?? true }),
            closesAt: task.closes_at ?? null,
            cycleId: isCampaign ? undefined : task.id,
            campaignId: isCampaign ? task.id : undefined,
            selfReflectionTitle: task.is_self_assessment_only ? task.title : undefined,
        });
    }
    for (const cycle of hub.value.active_360s ?? []) {
        items.push({
            id: `active-${cycle.id}`,
            kind: "active_360_status",
            title: `Your ${cycle.template.name} is collecting`,
            subtitle: "",
            chipLabel: "360",
            ctaLabel: "View",
            href: `/reflections/360/${cycle.id}`,
            closesAt: cycle.closes_at ?? null,
            progress: { responded: cycle.response_summary.responded, total: Math.max(cycle.response_summary.total, cycle.min_responses) },
        });
    }
    return items;
});

const selfTasks = computed(() => {
    if (!hub.value) {
        return [];
    }
    return (hub.value.pending_personal_tasks ?? []).filter((t) => t.is_self_assessment_only);
});

const sixtyTasks = computed(() => {
    if (!hub.value) {
        return [];
    }
    return (hub.value.pending_personal_tasks ?? []).filter((t) => !t.is_self_assessment_only);
});

// Fallback hrefs so a middle-click / new-tab still opens the full page — the
// hub UI hijacks the primary click via emits, but keeping real URLs preserves
// deep-link + browser-history behavior.
function hrefForTask(task: { kind: string; id: string; is_self_assessment_only: boolean }): string {
    if (task.kind === "self_reflection_cycle") {
        return `/reflections/360/${task.id}/self`;
    }
    if (task.kind === "self_reflection_campaign") {
        return `/reflections/360/start?mode=self&campaign_id=${task.id}`;
    }
    if (task.kind === "cycle_setup_campaign") {
        return `/reflections/360/start?campaign_id=${task.id}`;
    }
    if (task.kind === "cycle_setup") {
        return `/reflections/360/${task.id}/nominate`;
    }
    return `/reflections/360/${task.id}`;
}

// Backend `PendingPersonalTask.kind` values map onto UpcomingItem kinds. The
// mapping keeps the frontend's kind space closed (no free-form strings) so
// TypeScript catches missing branches when new kinds are added.
function mapTaskKind(kind: string, isSelfAssessmentOnly: boolean): UpcomingItem["kind"] {
    if (kind === "self_reflection_campaign") {
        return "self_reflection_campaign";
    }
    if (kind === "cycle_setup_campaign") {
        return "cycle_setup_campaign";
    }
    if (kind === "self_reflection_cycle") {
        return "self_reflection";
    }
    if (kind === "cycle_setup") {
        return "cycle_setup";
    }
    // Legacy / unknown — fall back to the closest kind that keeps navigation
    // working via the row's fallback href.
    return isSelfAssessmentOnly ? "self_reflection" : "cycle_setup";
}

async function onStartFromCampaign(payload: { campaignId: string; title: string; isSelfAssessmentOnly: boolean }) {
    // Materialize a real cycle from the campaign, then route the same way
    // onCycleCreated would. Self-only campaigns are auto-launched server-side
    // (see /cycles/from-campaign) so the taker mounts immediately.
    const { data, error } = await useFetch("/v1/reflections/cycles/from-campaign")
        .post(JSON.stringify({ campaign_id: payload.campaignId }), "application/json")
        .json<CycleEnvelopeResponse>();
    if (error.value || !data.value?.cycle) {
        loadError.value = "Could not start this reflection. Please refresh and try again.";
        return;
    }
    const cycle = data.value.cycle;
    // Push: the user came from the list, and the campaign row drops off it
    // once the cycle exists, so re-entering is harmless.
    if (payload.isSelfAssessmentOnly || cycle.is_self_assessment_only) {
        navigateTo({ kind: "take-self", cycleId: cycle.id, title: payload.title, closesAt: cycle.closes_at ?? null });
    } else {
        navigateTo({ kind: "nominate", cycleId: cycle.id });
    }
    void refreshHub();
}

async function onRemind(inviteId: string) {
    const cycle = hub.value?.active_360s?.find((c) => c.invites.some((i) => i.id === inviteId));
    if (!cycle) {
        return;
    }
    await useFetch(`/v1/reflections/cycles/${cycle.id}/invites/${inviteId}/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    }).json();
}

// The envelope is the card's cycle re-serialized, so swapping it in updates the
// "Closes" label in place without a hub round trip.
function onExtended(cycle: CycleForSubject) {
    if (!hub.value?.active_360s) {
        return;
    }
    hub.value.active_360s = hub.value.active_360s.map((existing) => (existing.id === cycle.id ? cycle : existing));
}

async function onWrapUp(cycleId: string) {
    // Server-side floor is 3 submitted rater responses; the button in
    // Active360Card enforces the same floor client-side to skip the round trip.
    const { data, error } = await useFetch(`/v1/reflections/cycles/${cycleId}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    }).json();
    if (error.value) {
        loadError.value = (data.value as { error?: string })?.error || "Could not wrap up this cycle.";
        return;
    }
    // Land on the results panel so the user sees the "Generating…" state
    // through to the finished report rather than a stale list row.
    navigateTo({ kind: "results", cycleId });
    void refreshHub();
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Slide-fade between hub views. `hub-forward` slides new content in from the
   right (drilling into a take/results panel), `hub-back` slides from the left
   (returning to the list). Reduced-motion users get an instant swap. */
    .hub-forward-enter-active,
    .hub-forward-leave-active,
    .hub-back-enter-active,
    .hub-back-leave-active {
        transition:
            opacity var(--motion-duration-normal) var(--motion-ease-standard),
            transform var(--motion-duration-view) var(--motion-ease-view);
    }

    @media (prefers-reduced-motion: no-preference) {
        .view-viewport:has(> .hub-forward-enter-active, > .hub-forward-leave-active, > .hub-back-enter-active, > .hub-back-leave-active) {
            mask-image: linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent);
        }
    }

    .hub-forward-enter-from {
        opacity: 0;
        transform: translateX(24px);
    }

    .hub-forward-leave-to {
        opacity: 0;
        transform: translateX(-24px);
    }

    .hub-back-enter-from {
        opacity: 0;
        transform: translateX(-24px);
    }

    .hub-back-leave-to {
        opacity: 0;
        transform: translateX(24px);
    }

    @media (prefers-reduced-motion: reduce) {
        .hub-forward-enter-active,
        .hub-forward-leave-active,
        .hub-back-enter-active,
        .hub-back-leave-active {
            transition: none;
        }

        .hub-forward-enter-from,
        .hub-forward-leave-to,
        .hub-back-enter-from,
        .hub-back-leave-to {
            transform: none;
        }
    }
}
</style>
